import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../components/ui/sheet';
import { toast } from 'sonner';
import { isSuperAdmin, getCurrentAccount } from '../../lib/auth';
import {
  TrendingDown, TrendingUp, RefreshCw, ArrowRight, FileText,
  Loader2, Wallet, Calendar, ExternalLink, Eye,
} from 'lucide-react';

const fmtC = (v) => v == null ? '\u2014' : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (d) => d ? new Date(d).toLocaleDateString() : '\u2014';

const TYPE_BADGE = {
  bill_payment: { label: 'Bill Payment', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  service_charge: { label: 'Service Fee', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  refund: { label: 'Refund', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  bank_credit: { label: 'Bank Credit', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

export default function CreditDebitReportPage() {
  const navigate = useNavigate();
  const currentAccount = getCurrentAccount();
  const isSuper = isSuperAdmin();

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billDrawerOpen, setBillDrawerOpen] = useState(false);
  const [billDrawerData, setBillDrawerData] = useState(null);

  const accountId = currentAccount?.id;

  const fetchData = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const params = { account_id: accountId, limit: 5000 };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data: d } = await api.get('/bank-statements/credit-debit-report', { params });
      setData(d);
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [accountId, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const stats = data?.stats || {};
  const lines = data?.lines || [];
  const totalRows = data?.total || 0;

  const openBillDrawer = (line) => {
    setBillDrawerData(line);
    setBillDrawerOpen(true);
  };

  const openBillFullPage = (bill_id) => {
    if (!bill_id) return;
    window.open(`/bills/${bill_id}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <MainLayout>
      <TooltipProvider delayDuration={150}>
      <div className="space-y-5" data-testid="credit-debit-report-page">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Credit / Debit Report</h2>
          <p className="text-sm text-muted-foreground">
            Customer ledger view: Credits from uploaded bank statements vs Debits from bills
            (payments + service charges + completed refunds).
          </p>
        </div>

        {/* Top Stats — bank statement style */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cd-top-stats">
          <div className="border rounded-lg p-4 bg-card">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-600">Current Balance</span>
            </div>
            <p className={`text-xl font-bold tabular-nums ${stats.current_balance < 0 ? 'text-red-700' : ''}`} data-testid="stat-current">{fmtC(stats.current_balance)}</p>
            <p className="text-[10px] text-muted-foreground mt-1">Credits − Debits (settled)</p>
          </div>
          <div className="border rounded-lg p-4 bg-card">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="h-3.5 w-3.5 text-violet-600" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-violet-600">Available Balance</span>
            </div>
            <p className={`text-xl font-bold tabular-nums ${stats.available_balance < 0 ? 'text-red-700' : ''}`} data-testid="stat-available">{fmtC(stats.available_balance)}</p>
            <p className="text-[10px] text-muted-foreground mt-1">Current − {fmtC(stats.pending_refund || 0)} pending refunds</p>
          </div>
          <div className="border rounded-lg p-4 bg-emerald-50/40">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600">Total Credits (YTD)</span>
            </div>
            <p className="text-xl font-bold text-emerald-700 tabular-nums" data-testid="stat-ytd-credits">{fmtC(stats.ytd_credits)}</p>
          </div>
          <div className="border rounded-lg p-4 bg-red-50/40">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="h-3.5 w-3.5 text-red-600" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-red-600">Total Debits (YTD)</span>
            </div>
            <p className="text-xl font-bold text-red-700 tabular-nums" data-testid="stat-ytd-debits">{fmtC(stats.ytd_debits)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="border rounded-lg p-3 bg-card flex items-center gap-3 flex-wrap" data-testid="cd-filters">
          <div className="flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs w-[140px]" data-testid="filter-date-from" />
            <span className="text-muted-foreground text-xs">to</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs w-[140px]" data-testid="filter-date-to" />
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="h-8 text-xs" data-testid="cd-refresh-btn">
            {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Refresh
          </Button>
          <span className="ml-auto text-[11px] text-muted-foreground tabular-nums">
            {loading ? '...' : `${totalRows.toLocaleString()} entries · Net ${fmtC(stats.net)}`}
          </span>
        </div>

        {/* 3-column ledger table */}
        <div className="border rounded-lg bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="cd-ledger-table">
              <thead className="bg-muted/30 border-b">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground w-[110px]">Date</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Line Item</th>
                  <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-emerald-600 w-[140px]">Credits</th>
                  <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-red-600 w-[140px]">Debits</th>
                  <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-blue-600 w-[140px]">Balance</th>
                  <th className="px-3 py-2 w-[80px]"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}><td colSpan={6} className="px-3 py-2"><Skeleton className="h-4 w-full" /></td></tr>
                  ))
                ) : lines.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-muted-foreground">
                      <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
                      <p>No ledger entries for the selected filters</p>
                      <p className="text-[11px] mt-2">Try uploading a bank statement under <span className="underline">Payments &gt; Upload Credit File</span></p>
                    </td>
                  </tr>
                ) : (
                  lines.map((l, i) => {
                    const badge = TYPE_BADGE[l.type] || { label: l.type, cls: 'bg-muted text-muted-foreground' };
                    const isBill = ['bill_payment', 'service_charge', 'refund'].includes(l.type);
                    const billId = l.meta?.bill_id;
                    return (
                      <Tooltip key={i}>
                        <TooltipTrigger asChild>
                          <tr
                            className="border-b border-border/40 hover:bg-muted/30 cursor-help"
                            data-testid={`cd-row-${i}`}
                          >
                            <td className="px-3 py-2 text-muted-foreground tabular-nums">{fmtD(l.post_date)}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-semibold uppercase tracking-wider ${badge.cls}`}>
                                  {badge.label}
                                </span>
                                <span className="truncate">{l.line_item}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-semibold">
                              {l.credit > 0 ? fmtC(l.credit) : ''}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-red-700 font-semibold">
                              {l.debit > 0 ? fmtC(l.debit) : ''}
                            </td>
                            <td className={`px-3 py-2 text-right tabular-nums font-mono text-[11px] ${l.balance < 0 ? 'text-red-700' : 'text-blue-700'}`} data-testid={`cd-balance-${i}`}>
                              {fmtC(l.balance)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {isBill && (
                                <button
                                  onClick={() => openBillDrawer(l)}
                                  className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1"
                                  data-testid={`cd-open-bill-${i}`}
                                >
                                  <Eye className="h-3 w-3" /> Open
                                </button>
                              )}
                            </td>
                          </tr>
                        </TooltipTrigger>
                        {isBill && l.meta && (
                          <TooltipContent side="right" align="center" className="text-xs p-3 max-w-[320px]">
                            <p className="font-semibold mb-1.5">{badge.label} — Bill Details</p>
                            <div className="space-y-0.5 text-[11px]">
                              <div className="flex justify-between gap-3"><span className="text-muted-foreground">Account #</span><span className="font-mono">{l.meta.account_number || '—'}</span></div>
                              <div className="flex justify-between gap-3"><span className="text-muted-foreground">Invoice #</span><span className="font-mono">{l.meta.invoice_number || '—'}</span></div>
                              <div className="flex justify-between gap-3"><span className="text-muted-foreground">Issue Date</span><span>{fmtD(l.meta.invoice_date)}</span></div>
                              <div className="flex justify-between gap-3"><span className="text-muted-foreground">Due Date</span><span>{fmtD(l.meta.due_date)}</span></div>
                              <div className="flex justify-between gap-3"><span className="text-muted-foreground">Total Due</span><span className="font-mono">{fmtC(l.meta.total_amount_due)}</span></div>
                              <div className="flex justify-between gap-3"><span className="text-muted-foreground">New Charges</span><span className="font-mono">{fmtC(l.meta.current_bill_amount)}</span></div>
                              <div className="flex justify-between gap-3"><span className="text-muted-foreground">Approved Amt</span><span className="font-mono">{fmtC(l.meta.payment_amount)}</span></div>
                            </div>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    );
                  })
                )}
              </tbody>
              {/* Totals row */}
              {!loading && lines.length > 0 && (
                <tfoot className="bg-muted/40 sticky bottom-0">
                  <tr className="border-t-2 border-border font-semibold">
                    <td colSpan={2} className="px-3 py-2 text-[11px] uppercase tracking-wider">Totals (filtered)</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{fmtC(stats.total_credits)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-red-700">{fmtC(stats.total_debits)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-mono ${stats.current_balance < 0 ? 'text-red-700' : 'text-blue-700'}`}>{fmtC(stats.current_balance)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {/* Bill Drawer (right-sliding) */}
      <Sheet open={billDrawerOpen} onOpenChange={setBillDrawerOpen}>
        <SheetContent side="right" className="w-[480px] p-0 flex flex-col" data-testid="cd-bill-drawer">
          <SheetHeader className="px-6 py-4 border-b border-border">
            <SheetTitle className="text-sm">Bill Details</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {billDrawerData?.meta ? (
              <div className="space-y-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Line Item</p>
                  <p className="text-sm">{billDrawerData.line_item}</p>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Account #</p>
                    <p className="font-mono">{billDrawerData.meta.account_number || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Invoice #</p>
                    <p className="font-mono">{billDrawerData.meta.invoice_number || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Issue Date</p>
                    <p>{fmtD(billDrawerData.meta.invoice_date)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Due Date</p>
                    <p>{fmtD(billDrawerData.meta.due_date)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Total Amount Due</p>
                    <p className="font-mono font-semibold">{fmtC(billDrawerData.meta.total_amount_due)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">New Charges</p>
                    <p className="font-mono">{fmtC(billDrawerData.meta.current_bill_amount)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Approved Amount</p>
                    <p className="font-mono">{fmtC(billDrawerData.meta.payment_amount)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Service Address</p>
                    <p className="text-[11px]">{billDrawerData.meta.service_address || '—'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs pt-3 border-t">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 mb-0.5">Credit on this Line</p>
                    <p className="font-mono font-bold text-emerald-700">{billDrawerData.credit > 0 ? fmtC(billDrawerData.credit) : '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-red-600 mb-0.5">Debit on this Line</p>
                    <p className="font-mono font-bold text-red-700">{billDrawerData.debit > 0 ? fmtC(billDrawerData.debit) : '—'}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No bill data available</p>
            )}
          </div>
          <div className="px-6 py-3 border-t border-border flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setBillDrawerOpen(false)} className="text-xs">
              Close
            </Button>
            {billDrawerData?.meta?.bill_id && (
              <Button size="sm" onClick={() => openBillFullPage(billDrawerData.meta.bill_id)} className="text-xs" data-testid="cd-open-bill-page">
                <ExternalLink className="h-3 w-3 mr-1" /> Open Bill Page
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
      </TooltipProvider>
    </MainLayout>
  );
}
